import { useMutation } from '@tanstack/react-query';

/* Apis */
import { user } from '@api/apis';

/* utils */
import { Logger } from '@utils/logger';
import { displayErrors, ResponseErrorNoticeType } from '@utils/errors';
import { updateContingencies } from '@store/queries/user/user.utils';

export type TRegisterProps = {
  address: string;
  address2: string;
  addressDetails?: string;
  email: string;
  firstName: string;
  hearAboutUs: string;
  lastName: string;
  password: string;
  passwordConfirmation: string;
  phone: string;
  subscribeForFeedback: boolean;
  subscribeForUpdates: boolean;
  first_name?: string;
};

export type TRegisterShortProps = {
  email: string;
  password?: string;
  first_name?: string;
  firstName?: string;
};

export default function useUserMutationRegister<T = TRegisterProps>(source?: string) {
  return useMutation({
    mutationFn: async (payload: T) => {
      const params = {
        user: payload,
        ...(source && { source }),
        sync_tasks: true,
      };
      const response = await user.registration(params);

      if (response.data?.user?.authToken) {
        /* This drops the cookie: silly workaround for dev: diff ports */
        await fetch(`/api/_nextassets_/current?auth_token=${response.data?.user?.authToken}`);
      }

      if (response.err) {
        const { status, response: { status: deepStatus = '' } = {} } = response.err;
        if ([status, deepStatus].includes(422)) {
          return response.data;
        }

        throw new Error(response);
      }

      return response.data;
    },
    /* Update user */
    onSuccess: data => {
      const { authToken } = data.user ?? {};

      if (authToken) {
        updateContingencies(data.user);
      }
    },

    onError: (error: ResponseErrorNoticeType | Error) => {
      displayErrors(error as ResponseErrorNoticeType);

      Logger('useUserMutationLogin')(error as Error);
    },
  });
}
